import {useTheme, useTransition} from '@logistics-frontend/blocks'
import {useMediaQuery} from '@logistics-frontend/blocks/legacy'
import {type FetchResult, type ServerError, useBaseErrorCatcher} from '@logistics-frontend/core/utils/network'
import {useRefresh} from '@logistics-frontend/hooks'
import {get} from '@logistics-frontend/utils'
import {flatten, pickBy} from 'lodash'
import {useCallback} from 'react'
import {useParams} from 'react-router-dom'

import {type OrderPoint, fetchPerformerPosition, fetchSharedRouteInfo} from 'src/api'
import type * as t from 'src/api/types'

import {RouterParams} from './types'
import {isFeedbackAction, isPerformerPositionAvailable} from './utils'

const SHARED_ROUTE_INFO_KEY = 'route/shared-route-info'

const getDelayFromXRefreshAfter = (response?: FetchResult<t.SharedRouteInfoResponse>) => {
  const delay = get('headers.x-refresh-after', response) || 3000
  return Number(delay)
}

const isDelivered = (response?: FetchResult<t.SharedRouteInfoResponse>): boolean => {
  const routePoints = (get('body.route_points', response) || []) as OrderPoint[]
  return routePoints.every((point: OrderPoint) => point.visit_status === 'visited')
}

export const useSharedRoute = () => {
  return useTransition<FetchResult<t.SharedRouteInfoResponse>, ServerError>(undefined, {key: SHARED_ROUTE_INFO_KEY})
}

export const useSharedRoteInfoPolling = (sharingKey: string) => {
  const [{payload, error, isPending}, startTransition] = useTransition(() => fetchSharedRouteInfo({key: sharingKey}), {
    key: SHARED_ROUTE_INFO_KEY,
  })
  const getSharedRouteInfo = useCallback(
    () => startTransition(() => fetchSharedRouteInfo({key: sharingKey})),
    [sharingKey, startTransition],
  )
  const stopPoll = isPending || Boolean(error) || isDelivered(payload)

  useRefresh(stopPoll ? undefined : getSharedRouteInfo, {interval: getDelayFromXRefreshAfter(payload)})
}

export const useSharedRouteInfo = () => {
  const [{payload}] = useSharedRoute()

  return payload?.body
}

const PERFORMER_POSITION_KEY = 'route/performer-position'

export const useSharedRoutePerformer = () => {
  const {sharingKey = ''} = useParams<RouterParams>()
  const info = useSharedRouteInfo()
  const isAvailable = isPerformerPositionAvailable(info)
  const errorCatcher = useBaseErrorCatcher()

  const [{payload, error}, startTransition] = useTransition<t.PerformerPositionResponseWithDirection, ServerError>(
    undefined,
    {
      key: PERFORMER_POSITION_KEY,
    },
  )

  const getPerformerPosition = useCallback(() => {
    startTransition(() => fetchPerformerPosition({key: sharingKey}), {onError: errorCatcher})
  }, [sharingKey, errorCatcher])

  useRefresh(isAvailable && !error ? getPerformerPosition : undefined, {interval: 3000})

  return payload
}

export const useIsMobile = (noSsr = true) => {
  const theme = useTheme()
  return useMediaQuery(theme.breakpoints.down('sm'), {noSsr})
}

export const useFeedbackReasons = (score: number): t.FeedbackReason[] => {
  const info = useSharedRouteInfo()
  const actionFeedback = info?.actions.find(isFeedbackAction)

  if (actionFeedback === undefined || actionFeedback.reasons === undefined) {
    return []
  }

  const allReasons = actionFeedback.reasons.reduce<Record<string, t.FeedbackReason[]>>((acc, reason) => {
    const key = reason.scores.toString()
    return {...acc, [key]: (acc[key] || []).concat(reason)}
  }, {})

  return flatten(Object.values(pickBy(allReasons, (_, key) => key.includes(score.toString()))))
}

export const useFeedbackSubtitle = (currentScore?: number): string | undefined => {
  const info = useSharedRouteInfo()
  const actionFeedback = info?.actions.find(isFeedbackAction)
  const score = currentScore || actionFeedback?.last_feedback?.score

  if (!actionFeedback || !score) {
    return
  }

  const allSubtitles = actionFeedback.subtitles.reduce<Record<string, t.FeedbackSubtitle[]>>((acc, subtitle) => {
    const key = subtitle.scores.toString()
    return {...acc, [key]: (acc[key] || []).concat(subtitle)}
  }, {})

  return flatten(Object.values(pickBy(allSubtitles, (_, key) => key.includes(score.toString()))))[0]?.title
}
